import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import io from 'socket.io-client';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import useCaseTicket from './useCaseTicket';
import CreateCaseTicketModal from './CreateCaseTicketModal';
import QuickCheckPanel from './QuickCheckPanel';
import ShopBadge from './ShopBadge';
import {
  ISSUE_TYPE_LABELS, STATUS_LABELS, STATUS_BADGE_STYLES, SHIPPING_CHANNEL_LABELS, ticketDisplayName,
} from './constants';

const StatusBadge = ({ status }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE_STYLES[status] || 'bg-gray-100 text-gray-700'}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

// Who the case is waiting on (computed by the backend — same rules as
// banners/digest). Plain role labels — the queue is shared, not personal.
const TURN_META = {
  admin: { label: 'แอดมิน', cls: 'bg-emerald-100 text-emerald-800' },
  owner: { label: 'หัวหน้า', cls: 'bg-blue-100 text-blue-800' },
  customer: { label: 'ลูกค้า', cls: 'bg-yellow-100 text-yellow-800' },
  warehouse: { label: 'Kluay', cls: 'bg-orange-100 text-orange-800' },
  // ตามของ already told: nobody owes anything until the sweep finds it or it comes due.
  hunting: { label: 'กำลังตามหา', cls: 'bg-sky-100 text-sky-800' },
};

const TurnBadge = ({ turn }) => {
  if (!turn) return <span className="text-gray-300">—</span>;
  const meta = TURN_META[turn];
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

const STATUS_TABS = [
  { id: 'all', label: 'ทั้งหมด' },
  { id: 'waiting_warehouse', label: '📦 รอ Kluay เช็ค' },
  { id: 'waiting_supervisor', label: 'รอหัวหน้าตอบ' },
  { id: 'need_info', label: 'รอข้อมูลเพิ่ม' },
  { id: 'waiting_customer', label: 'รอลูกค้าตอบ' },
  { id: 'in_progress', label: 'กำลังดำเนินการ' },
  { id: 'customer_update', label: 'รอแอดมินแจ้งลูกค้า' },
  { id: 'customer_informed', label: 'แจ้งลูกค้าแล้ว' },
  { id: 'customer_confirmed', label: 'ลูกค้ายืนยันแล้ว' },
  { id: 'closed', label: 'ปิดแล้ว' },
  { id: 'report', label: '📊 รายงาน' },
];

// Groups a list of tickets by an arbitrary key, counting how many fall in each bucket.
const countBy = (list, getKey) => {
  const counts = {};
  list.forEach((item) => {
    const key = getKey(item) || 'ไม่ระบุ';
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]);
};

const CaseTicket = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState } = useAuth();
  const viewerRole = authState?.user?.role;
  const isMyTurn = (t) =>
    (viewerRole === 'superuser' && t.turn === 'owner') || (viewerRole !== 'superuser' && t.turn === 'admin');
  const { caseTickets, fetchCaseTickets: fetchAll } = useCaseTicket();
  // The selected shop's cases + the ones I took in either shop (backend scope).
  const shopName = useSelector((state) => state.shop.name);
  const fetchCaseTickets = () => fetchAll({ shop: shopName });
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'all');
  const [typeFilter, setTypeFilter] = useState('all'); // all | customer | internal
  // Day-to-day work is about open cases — closed ones are archive, hidden by
  // default (the ปิดแล้ว tab still shows them regardless of this toggle).
  const [openOnly, setOpenOnly] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showQuickCheck, setShowQuickCheck] = useState(false);
  // Hand-off from the quick check: the modal opens as a ตามของ case with the
  // customer, the unresolved parcels and (when clear) the round already filled.
  const [prefill, setPrefill] = useState(null);

  useEffect(() => { fetchCaseTickets(); }, [shopName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}/admin-dashboard`, { withCredentials: true });
    socket.on('new_case_ticket', () => fetchCaseTickets());
    socket.on('case_ticket_updated', () => fetchCaseTickets());
    socket.on('case_claim_updated', ({ caseType } = {}) => {
      if (caseType === 'case_ticket') fetchCaseTickets();
    });
    return () => socket.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const typeFiltered = typeFilter === 'all'
    ? caseTickets
    : caseTickets.filter((t) => (typeFilter === 'internal' ? t.type === 'internal' : t.type !== 'internal'));

  const filtered = ((activeTab === 'all' || activeTab === 'report')
    ? typeFiltered.filter((t) => !(openOnly && t.status === 'closed'))
    : typeFiltered.filter((t) => t.status === activeTab))
    // Your-turn cases float to the top — the list doubles as a work queue.
    .slice()
    .sort((a, b) => (isMyTurn(b) - isMyTurn(a)) || (new Date(b.createdAt) - new Date(a.createdAt)));

  const renderReport = () => {
    // Operational quality data = customer cases only; internal bug/feature
    // tickets must not pollute the damage-pattern stats.
    const customerTickets = caseTickets.filter((t) => t.type !== 'internal');
    const total = customerTickets.length;
    const closedCount = customerTickets.filter((t) => t.status === 'closed').length;
    const damagedTickets = customerTickets.filter((t) => t.issueType === 'damaged');

    const byIssueType = countBy(customerTickets, (t) => ISSUE_TYPE_LABELS[t.issueType] || t.issueType);
    const byShippingChannel = countBy(damagedTickets, (t) => SHIPPING_CHANNEL_LABELS[t.shippingChannel] || t.shippingChannel);
    const byProductGroup = countBy(customerTickets, (t) => t.productGroup);
    const byShipMode = countBy(customerTickets, (t) => (t.isAir === true ? '✈️ แอร์' : t.isAir === false ? '🚢 เรือ' : null));

    const renderCountList = (rows, emptyLabel) => rows.length === 0
      ? <p className="text-gray-400 text-sm">{emptyLabel}</p>
      : (
        <div className="flex flex-col gap-1">
          {rows.map(([label, count]) => (
            <div key={label} className="flex items-center justify-between text-sm border-b border-gray-100 py-1">
              <span>{label}</span>
              <span className="font-semibold text-blue-700">{count}</span>
            </div>
          ))}
        </div>
      );

    return (
      <div>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-600 font-medium">เคสลูกค้าทั้งหมด</p>
            <p className="text-2xl font-bold text-blue-700">{total} เคส</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <p className="text-sm text-green-600 font-medium">ปิดแล้ว</p>
            <p className="text-2xl font-bold text-green-700">{closedCount} เคส</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
            <p className="text-sm text-orange-600 font-medium">ยังไม่ปิด</p>
            <p className="text-2xl font-bold text-orange-700">{total - closedCount} เคส</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-2">ตามประเภทปัญหา</h3>
            {renderCountList(byIssueType, 'ยังไม่มีข้อมูล')}
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-2">ช่องทางขนส่ง (เฉพาะสินค้าเสียหาย)</h3>
            {renderCountList(byShippingChannel, 'ยังไม่มีเคสสินค้าเสียหาย')}
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-2">ตามกลุ่มสินค้า</h3>
            {renderCountList(byProductGroup, 'ยังไม่มีข้อมูล')}
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-2">รอบเรือ/แอร์</h3>
            {renderCountList(byShipMode, 'ยังไม่มีข้อมูล')}
          </div>
        </div>
      </div>
    );
  };

  const columnDefs = [
    { headerName: '#', field: 'id', width: 80 },
    {
      headerName: 'รอดำเนินการโดย', field: 'turn', width: 140,
      cellRenderer: (p) => <TurnBadge turn={p.value} />,
    },
    {
      headerName: 'ลูกค้า/หัวข้อ', flex: 1,
      valueGetter: (p) => ticketDisplayName(p.data),
      cellRenderer: (p) => (p.data.type === 'internal' ? (
        <span>
          <span className="mr-1.5 px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">🛠️ ภายใน</span>
          {p.value}
        </span>
      ) : p.value),
    },
    {
      headerName: 'ประเภท', field: 'issueType', flex: 1,
      valueFormatter: (p) => ISSUE_TYPE_LABELS[p.value] || p.value,
    },
    {
      headerName: 'สถานะ', field: 'status', flex: 1,
      cellRenderer: (p) => <StatusBadge status={p.value} />,
    },
    {
      headerName: 'ผู้รับเคส', field: 'claimedByName', flex: 1,
      cellRenderer: (p) => p.value || <span className="text-gray-300">—</span>,
    },
    {
      headerName: 'วันที่แจ้ง', flex: 1,
      valueGetter: (p) => p.data.reportedDate || p.data.createdAt,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      headerName: 'ร้าน', width: 90,
      valueGetter: (p) => p.data.customer?.shop || null,
      cellRenderer: (p) => <ShopBadge shop={p.value} />,
    },
  ];

  return (
    <div className="ag-theme-alpine" style={{ width: '100%' }}>
      <div className="flex items-center justify-between mb-3">
        <h2>เคสลูกค้า</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowQuickCheck((v) => !v)}
            className={`px-4 py-2 rounded-lg font-medium border ${
              showQuickCheck ? 'bg-sky-700 text-white border-sky-700' : 'bg-white text-sky-700 border-sky-300 hover:bg-sky-50'
            }`}>
            🔍 เช็คของลูกค้า
          </button>
          <button onClick={() => setIsModalOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium">
            + เปิดเคสใหม่
          </button>
        </div>
      </div>

      {showQuickCheck && (
        <QuickCheckPanel onOpenCase={(p) => { setPrefill(p); setIsModalOpen(true); }} />
      )}

      <div className="flex gap-2 mb-2 flex-wrap">
        {[
          { id: 'all', label: 'ทุกประเภท' },
          { id: 'customer', label: '🧑‍💼 เคสลูกค้า' },
          { id: 'internal', label: '🛠️ ภายใน' },
        ].map((t) => (
          <button key={t.id} onClick={() => setTypeFilter(t.id)}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              typeFilter === t.id ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-500 border-gray-300'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {STATUS_TABS.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 rounded text-sm font-medium border ${
              activeTab === tab.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'
            }`}>
            {tab.label}
          </button>
        ))}
        {activeTab === 'all' && (
          <label className="flex items-center gap-1.5 text-sm text-gray-600 ml-2 cursor-pointer select-none">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
            เฉพาะเคสที่ยังไม่ปิด
          </label>
        )}
      </div>

      {activeTab === 'report' ? renderReport() : (
        <AgGridReact
          columnDefs={columnDefs}
          rowData={filtered}
          domLayout="autoHeight"
          defaultColDef={{ resizable: true, sortable: true }}
          onRowClicked={(e) => navigate(`/case-ticket/${e.data.id}`)}
          rowStyle={{ cursor: 'pointer' }}
          // Internal cases tinted so they stand apart in the ทุกประเภท list.
          getRowStyle={(p) => (p.data.type === 'internal' ? { background: '#FAF5FF' } : undefined)}
        />
      )}

      <CreateCaseTicketModal
        isOpen={isModalOpen}
        prefill={prefill}
        onClose={() => { setIsModalOpen(false); setPrefill(null); }}
        // Stay on the list — the case is the supervisor's (or the warehouse's)
        // problem now; the admin's create-session is done.
        onCreated={() => { fetchCaseTickets(); setShowQuickCheck(false); }}
      />
    </div>
  );
};

export default CaseTicket;
